import { useContext } from "react";
import { FaDownload, FaLocationCrosshairs } from "react-icons/fa6";
import { useNavigate } from "@tanstack/react-router";
import classNames from "classnames";

import type { DataAssetWithManifestAndRealAssetDeploymentResponse } from "@/apiClient/models/DataAssetWithManifestAndRealAssetDeploymentResponse";
import { AvailableFeatureFlags } from "@/apiClient";
import {
  prepAssetDownloadURL,
  prepAssetPreviewURL,
} from "@/apiUtils/downloadUrls";
import UserContext from "@/contexts/user";
import useDataAssetSealogSearch from "@/hooks/useDataAssetSealogSearch";
import { hasFeatureFlag } from "@/utils";
import FilePreviewDialog from "@/components/preview/filePreviewDialog";
import { assetIsPreviewable } from "@/components/preview/utils";
import { retrieveSealogTimestamps } from "@/components/dataAsset/retrieveSealogTimestamps";

type DataAssetPreviewDialogProps = {
  isOpen: boolean;
  dataAsset: DataAssetWithManifestAndRealAssetDeploymentResponse | null;
  initialPreviewUrl?: string | null;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  navigatingDirection: "previous" | "next" | null;
  previousUnavailableMessage?: string;
  nextUnavailableMessage?: string;
  sealogSearchTerm: string;
  sealogEventValues: string[];
  sealogEventFreeTexts: string[];
  sealogEventOptionNames: string[];
  sealogEventOptionValues: string[];
  regex: boolean;
};

const getFilename = (path: string) => path.split("/").pop() || path;

export default function DataAssetPreviewDialog({
  isOpen,
  dataAsset,
  initialPreviewUrl = null,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  navigatingDirection,
  previousUnavailableMessage,
  nextUnavailableMessage,
  sealogSearchTerm,
  sealogEventValues,
  sealogEventFreeTexts,
  sealogEventOptionNames,
  sealogEventOptionValues,
  regex,
}: DataAssetPreviewDialogProps) {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { assetType, canPreview: fileTypeCanPreview } =
    assetIsPreviewable(dataAsset);
  const isVideo = assetType === "video/mp4";
  const assetUrlTarget = dataAsset?.public_id
    ? { public_id: dataAsset.public_id }
    : null;
  const canPreview = fileTypeCanPreview && !!assetUrlTarget;
  const basePreviewUrl = assetUrlTarget
    ? prepAssetPreviewURL(assetUrlTarget)
    : "";
  const downloadUrl = assetUrlTarget
    ? prepAssetDownloadURL(assetUrlTarget)
    : "";
  const hasDeploymentFeature = hasFeatureFlag(
    user,
    AvailableFeatureFlags.REAL_ASSET_DEPLOYMENT_DETAIL
  );
  const {
    query: { data: sealogData },
  } = useDataAssetSealogSearch({
    dataAssetPublicId: dataAsset?.public_id,
    sealogSearchTerm,
    sealogEventValues,
    sealogEventFreeTexts,
    sealogEventOptionNames,
    sealogEventOptionValues,
    regex,
    enabled: isOpen && isVideo,
  });

  const timestamps =
    isVideo && sealogData?.items && dataAsset?.path
      ? retrieveSealogTimestamps(sealogData.items, dataAsset.path)
      : [];

  const currentPreviewUrl =
    initialPreviewUrl ||
    (isVideo && timestamps.length > 0
      ? `${basePreviewUrl}#t=${timestamps[0]}`
      : basePreviewUrl);

  const handleDeploymentClick = () => {
    const deployment = dataAsset?.real_asset_deployments?.[0];
    const realAssetId = deployment?.real_asset?.public_id;
    const deploymentId = deployment?.public_id;

    if (!realAssetId || !deploymentId) {
      return;
    }

    onClose();
    navigate({ to: `/realassets/${realAssetId}/${deploymentId}` });
  };

  if (!dataAsset) {
    return null;
  }

  const filename = getFilename(dataAsset.path);

  return (
    <FilePreviewDialog
      isOpen={isOpen}
      onClose={onClose}
      title={filename}
      subtitle={dataAsset.path}
      previewMeta={{ assetType, canPreview }}
      previewUrl={currentPreviewUrl || basePreviewUrl}
      onPrevious={onPrevious}
      onNext={onNext}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      navigatingDirection={navigatingDirection}
      previousUnavailableMessage={previousUnavailableMessage}
      nextUnavailableMessage={nextUnavailableMessage}
      actions={
        <>
          <button
            aria-label="Download file"
            className="ox-button flex min-h-10 items-center rounded-md px-4 py-2"
            onClick={() => {
              window.location.href = downloadUrl;
            }}
          >
            <FaDownload className="mr-2" />
            Download
          </button>
          {dataAsset.real_asset_deployments?.length > 0 && (
            <button
              className={classNames(
                "ox-button flex min-h-10 items-center rounded-md px-4 py-2",
                hasDeploymentFeature
                  ? "text-blue-200"
                  : "cursor-not-allowed text-gray-400"
              )}
              disabled={!hasDeploymentFeature}
              onClick={hasDeploymentFeature ? handleDeploymentClick : undefined}
              aria-label="See associated deployment"
            >
              <FaLocationCrosshairs className="mr-2" />
              {hasDeploymentFeature
                ? "See Associated Deployment"
                : "See Deployment (Coming Soon)"}
            </button>
          )}
        </>
      }
    />
  );
}
